import React, { useState, useEffect } from "react";
import {
  Box,
  Heading,
  Grid, // Changed to Grid
  Button,
  Flex,
  VStack,
  Select,
  Input,
  useColorModeValue,
  useDisclosure,
  Spinner,
  Alert,
  AlertIcon,
  Text,
  Container,
  Card, // Import Card
  CardBody, // Import CardBody
  FormControl,
  FormLabel,
  HStack,
  GridItem,
  CheckboxGroup,
  Checkbox,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useBreakpointValue,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Image,
  IconButton,
} from "@chakra-ui/react";
import { ChevronRightIcon, ChevronLeftIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { motion } from "framer-motion";
import { FaPlusCircle, FaSearch } from "react-icons/fa";
import {
  collection,
  onSnapshot,
  query,
  where,
  firestore,
} from "../firebase/firebaseConfig";
import ListingCard from "../components/ListingCard";
import ListingDetailsModal from "../components/ListingDetailsModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getFlagByCountryCode } from "../utils/useFlags";
import countryCodes from "../components/data/CountryCodes.json";
import { useTranslation } from "react-i18next";

const ListingPage = () => {
  const { t, i18n } = useTranslation();

  const navigate = useNavigate(); // For navigation to new page
  const [searchParams, setSearchParams] = useSearchParams();
  
  const textColor = useColorModeValue("brand.200", "white");
  const isSmallScreen = useBreakpointValue({ base: true, md: false });

  const bgColor = useColorModeValue("brand.200", "blue.300");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedListing, setSelectedListing] = useState(null);
  const [listings, setListings] = useState([]);
  const [filteredListings, setFilteredListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
    const listingsPerPage = 5; // Adjust as needed

  // State for selected country and currency
  const [selectedCountry, setSelectedCountry] = useState({ name: "", code: "" });
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [uniqueCurrencies, setUniqueCurrencies] = useState([]);
  const [filterLocation, setFilterLocation] = useState("");
  const [filterAmount, setFilterAmount] = useState("");
  const [filterPaymentMethod, setFilterPaymentMethod] = useState("Cash");
  const [searchQuery, setSearchQuery] = useState(""); // ✅ Initialize searchQuery
  
  // Allowed countries (based on country names or codes)
  const allowedCountries = ["United States", "Canada", "Cameroon"];

  // Allowed currencies (by currency codes)
  const allowedCurrencies = ["USD", "CAD", "XAF"];

  // Handle Country Selection
  const handleCountrySelect = (country) => {
    setSelectedCountry({ name: country.name, code: country.code });
  };
    
  // Check if URL contains a listing ID and open modal
  useEffect(() => {
    const listingId = searchParams.get("listing");
    if (listingId) {
      const matchedListing = listings.find((l) => l.id === listingId);
      if (matchedListing) {
        setSelectedListing(matchedListing);
        onOpen();
      }
    }
  }, [searchParams, listings, onOpen]);

  // Initialize country and currency defaults
  useEffect(() => {

    // Filter countries based on the allowedCountries array
    const filteredCountries = countryCodes.filter((country) =>
      allowedCountries.includes(country.name)
    );

    // Set default country (first entry in filteredCountries)
    if (filteredCountries.length > 0) {
      setSelectedCountry(filteredCountries[0]);
    }

    // Generate and set filtered currency list
    const currencyMap = {};
    filteredCountries.forEach((country) => {
      if (
        country.currency &&
        allowedCurrencies.includes(country.currency) &&
        !currencyMap[country.currency]
      ) {
        currencyMap[country.currency] = {
          code: country.currency,
          countryCode: country.code,
        };
      }
    });

    const currencyList = Object.values(currencyMap);
    setUniqueCurrencies(currencyList);

    // Set default currency to the first available one
    if (currencyList.length > 0) {
      setSelectedCurrency(currencyList[0]);
    }

    // Set default country (first entry in countryCodes)
    /* if (countryCodes.length > 0) {
      setSelectedCountry(countryCodes[0]);
    } */

    // Generate and set default currency list
    /* const currencyMap = {};
    countryCodes.forEach((country) => {
      if (country.currency && !currencyMap[country.currency]) {
        currencyMap[country.currency] = {
          code: country.currency,
          countryCode: country.code,
        };
      }
    }); */

    //const currencyList = Object.values(currencyMap);
    //setUniqueCurrencies(currencyList);

    // Set default currency to the first available one
    /* if (currencyList.length > 0) {
      setSelectedCurrency(currencyList[0]);
    } */
  }, []);

  // Handle currency selection
  const handleCurrencySelect = (currency) => {
    setSelectedCurrency(currency);
  };



  useEffect(() => {
    const fetchListings = () => {
      try {
        const listingsRef = collection(firestore, "listings");
        const listingsQuery = query(listingsRef, where("status", "in", ["active", "matched"]));

        const unsubscribe = onSnapshot(listingsQuery, (snapshot) => {
          const listingsData = snapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }));
          setListings(listingsData);
          setFilteredListings(listingsData);
          setLoading(false);
        });

        return unsubscribe;
      } catch (err) {
        console.error(t("listingpage.fetch_error"), err);
        setError(t("listingpage.fetch_error_message"));
        setLoading(false);
      }
    };

    const unsubscribe = fetchListings();
    return () => unsubscribe(); // Unsubscribe from Firestore listener on component unmount
  }, []);

  // Open listing details modal
  const openListingDetails = (listing) => {
    setSelectedListing(listing);
    setSearchParams({ listing: listing.id }); // ✅ Update URL with listing ID
    onOpen();
  };

  // Close modal and clear listing ID from URL
  const closeListingDetails = () => {
    setSearchParams({}); // ✅ Remove listing ID from URL
    onClose();
  };


  // Calculate the listings to display on the current page
  useEffect(() => {
    if (!listings.length) return;

    let filtered = [...listings];

    if (selectedCountry?.name) {
      filtered = filtered.filter((listing) => listing.country === selectedCountry.name);
    }

    if (selectedCurrency?.code) {
      filtered = filtered.filter((listing) => listing.currency === selectedCurrency.code);
    }

    if (filterLocation.trim()) {
      filtered = filtered.filter((listing) =>
        listing.location?.toLowerCase().includes(filterLocation.toLowerCase())
      );
    }

    if (filterPaymentMethod) {
      filtered = filtered.filter((listing) => (listing.paymentMethod || "Cash").toLowerCase().includes((searchQuery || "").toLowerCase()));
    }

    if (filterAmount.trim() && !isNaN(filterAmount)) {
      filtered = filtered.filter((listing) => Number(listing.amount) === Number(filterAmount));
    }

    setFilteredListings(filtered);
    setCurrentPage(1);
  }, [selectedCountry, selectedCurrency, filterLocation, filterPaymentMethod, filterAmount, listings]);
    
  
  const indexOfLastListing = currentPage * listingsPerPage;
  const indexOfFirstListing = indexOfLastListing - listingsPerPage;
  const currentListings = filteredListings.slice(indexOfFirstListing, indexOfLastListing);

  // Change Page
  const nextPage = () => {
    if (currentPage < Math.ceil(filteredListings.length / listingsPerPage)) {
      setCurrentPage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };


  return (
    <Box bg="brand.200" color="brand.500">
      {/* Hero Section */}
      <Box
        py={12}
        px={8}
        textAlign="center"
        // bgGradient="linear(to-r, blue.500, blue.400)"
        color="brand.300"
        borderRadius="lg"
      >
        <Heading
          size="2xl"
          fontWeight="bold"
          mb={4}
        // textShadow="0px 2px 5px rgba(0, 0, 0, 0.3)"
        >
          {t("listingpage.hero_title")}
        </Heading>
        <Text
          fontSize="lg"
          mb={6}
        // textShadow="0px 1px 3px rgba(0, 0, 0, 0.2)"
        >
          {t("listingpage.hero_description")}
        </Text>

        <Flex justify="center" align="center" gap={4}>
          {/* Highlighted Post Listing Button */}
          <Button
            leftIcon={<FaPlusCircle />}
            bg="brand.300"
            color="white"
            fontWeight="bold"
            _hover={{ bg: "brand.100", color: "brand.300", transform: "scale(1.05)" }}
            size="lg"
            shadow="lg"
            transition="0.3s"
            onClick={() => navigate("/personal/post-listing")} // Redirect to post listing page
          >
            {t("listingpage.post_listing_button")}
          </Button>

          {/* Secondary Explore Listings Button */}
          {/* <Button
            leftIcon={<FaSearch />}
            colorScheme="whiteAlpha"
            variant="outline"
            size="lg"
            _hover={{ bg: "blue.600", transform: "scale(1.05)" }}
            transition="0.3s"
            onClick={() => navigate("/personal/listings")} // Redirect to explore listings
          >
            Explore Deals
          </Button> */}
        </Flex>
      </Box>



      {/* Filters Section */}
      <Box py={6} px={4} bg="brand.100" borderRadius="2xl">
        <Container maxW="container.xl">
          <VStack spacing={6} align="stretch" as={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
            <Grid templateColumns={{ base: 'repeat(1, 1fr)', md: 'repeat(5, 1fr)' }} gap={6}>
              {/* Sending Money To Field */}
              <GridItem>
                <FormControl>
                  <FormLabel pb={{ base: '1', md: '0' }} mb={{ base: '2', md: '1' }} color="brand.300" fontSize="lg">
                  {t("listingpage.sending_money_label")}
                  </FormLabel>
                  <Menu>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      bg="white"
                      color="brand.500"
                      fontSize="lg"
                      fontWeight="bold"
                      w="full"
                      size="md"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      rounded="md"
                    >
                      {selectedCountry.name ? (
                        <HStack spacing={2}>
                          <Image
                            src={getFlagByCountryCode(selectedCountry.code)}
                            alt={selectedCountry.name}
                            boxSize="20px"
                            borderRadius="full"
                          />
                          <Text>{selectedCountry.name}</Text>
                        </HStack>
                      ) : (
                        "Loading..."
                      )}
                    </MenuButton>
                    <MenuList>
                      {countryCodes
                        .filter((country) => allowedCountries.includes(country.name))
                        .map((country) => (
                          <MenuItem
                            key={country.code}
                            value={country.code}
                            onClick={() => setSelectedCountry(country)}
                          >
                            <Image
                              src={getFlagByCountryCode(country.code)}
                              alt={`${country.name} flag`}
                              boxSize="20px"
                              mr={2}
                            />
                            {country.name}
                          </MenuItem>
                        ))}
                    </MenuList>
                  </Menu>
                </FormControl>
              </GridItem>




              {/* Location Field */}
              <GridItem>
                <FormControl>
                  <FormLabel pb={1} color="brand.300" fontSize="lg" mb={isSmallScreen ? 2 : 0}>
                  {t("listingpage.location_label")}
                  </FormLabel>
                  <Input
                    type="text"
                    placeholder={t("listingpage.location_placeholder")}
                    bg="white"
                    fontSize="lg"
                    fontWeight="bold"
                    minW="150px"
                    h="42px"
                  />
                </FormControl>
              </GridItem>

              {/* Amount and Currency Field */}
              <GridItem>
                <FormControl>
                  <FormLabel pb={1} color="brand.300" fontSize="lg" mb={isSmallScreen ? 2 : 0}>
                  {t("listingpage.amount_label")}
                  </FormLabel>
                  <Grid templateColumns="repeat(2, 1fr)" gap={2}>
                    {/* Amount Input */}
                    <Input
                      type="text"
                      placeholder={t("listingpage.amount_placeholder")} 
                      bg="white"
                      fontSize="lg"
                      fontWeight="bold"
                      h="42px"
                    />

                    {/* Custom Currency Select */}
                    <Menu>
                      <MenuButton
                        as={Button}
                        rightIcon={<ChevronDownIcon />}
                        bg="white"
                        color="brand.500"
                        fontSize="md"
                        fontWeight="bold"
                        w="full"
                        size="md"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        rounded="md"
                      >
                        {selectedCurrency ? (
                          <HStack spacing={2}>
                            <Image
                              src={getFlagByCountryCode(selectedCurrency.countryCode)}
                              alt={`${selectedCurrency.code} flag`}
                              boxSize="20px"
                            />
                            <Text>{selectedCurrency.code}</Text>
                          </HStack>
                        ) : (
                          "Loading..."
                        )}
                      </MenuButton>
                      <MenuList>
                        {uniqueCurrencies.map((currency) => (
                          <MenuItem
                            key={currency.code}
                            onClick={() => handleCurrencySelect(currency)}
                          >
                            <Image
                              src={getFlagByCountryCode(currency.countryCode)}
                              alt={`${currency.code} flag`}
                              boxSize="20px"
                              mr={2}
                            />
                            {currency.code}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </Grid>
                </FormControl>
              </GridItem>




              {/* Payment Method Field */}
              <GridItem>
                <FormControl>
                  <FormLabel pb={1} color="brand.300" fontSize="lg" mb={isSmallScreen ? 2 : 0}>
                  {t("listingpage.payment_method_label")}
                  </FormLabel>
                  <Select
                    variant="outline"
                    bg="white"
                    fontSize="lg"
                    fontWeight="bold"
                    defaultValue="Cash"
                  >
                    <option value="cash">{t("listingpage.cash_option")}</option>
                    <option value="bank-transfer">{t("listingpage.bank_transfer_option")}</option>
                    <option value="mtn-momo">{t("listingpage.mtn_momo_option")}</option>
                    <option value="orange-money">{t("listingpage.orange_money_option")}</option>
                    <option value="paypal">{t("listingpage.paypal_option")}</option>
                  </Select>
                </FormControl>
              </GridItem>

              {/* Find Peer Button */}
              <GridItem justifySelf="center" alignSelf="end">
                <Button
                  leftIcon={<FaSearch />}
                  size="sm"
                  colorScheme="brand.300"
                  color="white"
                  bg="brand.300"
                  w="100%"
                  p={6}
                  transition="0.3s"
                  fontSize="md"
                >
                  {t("listingpage.find_peer_button")}
                  </Button>
              </GridItem>
            </Grid>
          </VStack>
        </Container>
      </Box>
      {/* Listings Section */}
      <Box py={8} px={4}>
        <Container maxW="container.xl"> {/* Container for Listings */}
          {/* <Heading textAlign="center" mb={10} color={textColor}>
            Active Listings
          </Heading> */}
          {loading ? (
            <Flex justify="center" mt={6}>
              <Spinner size="xl" />
            </Flex>
          ) : error ? (
            <Alert status="error" borderRadius="md" maxW="600px" mx="auto" mt={4}>
              <AlertIcon />
              {error}
            </Alert>
          ) : filteredListings.length === 0 ? (
            <Text color={textColor} textAlign="center">
              {t("listingpage.no_listings_message")}
            </Text>
          ) : (
            <>
            <Grid // Use Grid instead of SimpleGrid
              templateColumns={{ base: "1fr", md: "repeat(1, 1fr)" }} // 1 column on small, 2 on medium and larger
              gap={8}
            >
                {currentListings.map((listing) => (
                <motion.div
                  key={listing.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  <ListingCard listing={listing} onOpenDetails={openListingDetails} />
                  </motion.div>
                ))}
              </Grid>

            {/* Pagination Controls */}
            <Flex justify="center" align="center" mt={8} gap={4}>
                <IconButton
                  icon={<ChevronLeftIcon />}
                  onClick={prevPage}
                  isDisabled={currentPage === 1}
                  aria-label="Previous Page"
                />
                <Text fontWeight="bold">
                  Page {currentPage} of {Math.ceil(listings.length / listingsPerPage)}
                </Text>
                <IconButton
                  icon={<ChevronRightIcon />}
                  onClick={nextPage}
                  isDisabled={currentPage === Math.ceil(listings.length / listingsPerPage)}
                  aria-label="Next Page"
                />
              </Flex>
            </>
          )}
        </Container>
      </Box>

      {/* Listing Details Modal */}
      <ListingDetailsModal isOpen={isOpen} onClose={closeListingDetails} listing={selectedListing} />
    </Box>
  );
};

export default ListingPage;
